import { extendTheme, type ThemeConfig } from '@chakra-ui/react';

import { darkPalette, lightPalette } from './color-theme';
import { Badge } from './components/badge';
import { Button } from './components/button';
import { Heading } from './components/heading';
import { thinScrollbar } from './tokens';

const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: false,
};

const semanticColor = (lightVal: string, darkVal: string) => ({
  default: lightVal,
  _dark: darkVal,
});

export const theme = extendTheme({
  config,

  fonts: {
    heading: 'var(--font-display), "Outfit", -apple-system, sans-serif',
    body: 'var(--font-body), -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    mono: 'var(--font-mono), ui-monospace, SFMono-Regular, Menlo, monospace',
  },

  semanticTokens: {
    colors: {
      'bg.canvas': semanticColor(lightPalette.canvas, darkPalette.canvas),
      'bg.surface': semanticColor(lightPalette.surface, darkPalette.surface),
      'bg.elevated': semanticColor(lightPalette.elevated, darkPalette.elevated),

      'line.subtle': semanticColor(
        lightPalette.lineSubtle,
        darkPalette.lineSubtle,
      ),
      'line.strong': semanticColor(
        lightPalette.lineStrong,
        darkPalette.lineStrong,
      ),

      'text.primary': semanticColor(
        lightPalette.textPrimary,
        darkPalette.textPrimary,
      ),
      'text.muted': semanticColor(
        lightPalette.textMuted,
        darkPalette.textMuted,
      ),
      'text.faint': semanticColor(
        lightPalette.textFaint,
        darkPalette.textFaint,
      ),

      'accent.500': semanticColor(lightPalette.accent, darkPalette.accent),
      'accent.600': semanticColor(
        lightPalette.accentSoft,
        darkPalette.accentSoft,
      ),
      'accent.dim': semanticColor(
        lightPalette.accentDim,
        darkPalette.accentDim,
      ),
      'accent.line': semanticColor(
        lightPalette.accentLine,
        darkPalette.accentLine,
      ),
      'accent.glow': semanticColor(
        lightPalette.accentGlow,
        darkPalette.accentGlow,
      ),
    },
  },

  components: {
    Button,
    Heading,
    Badge,
  },

  styles: {
    global: (props: { colorMode: 'light' | 'dark' }) => ({
      'html, body': {
        bg:
          props.colorMode === 'dark' ? darkPalette.canvas : lightPalette.canvas,
        color:
          props.colorMode === 'dark'
            ? darkPalette.textPrimary
            : lightPalette.textPrimary,
        overflowX: 'hidden',
        ...thinScrollbar,
      },
      body: {
        fontFamily: 'body',
        fontSize: '16px',
        lineHeight: 1.6,
        fontFeatureSettings: '"cv11", "ss01", "ss03"',
      },
      '*::selection': {
        bg:
          props.colorMode === 'dark'
            ? darkPalette.accentDim
            : lightPalette.accentDim,
        color:
          props.colorMode === 'dark' ? darkPalette.accent : lightPalette.accent,
      },
      a: { color: 'inherit', textDecoration: 'none' },
    }),
  },
});

export type AppTheme = typeof theme;
